<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import MonthPickerComponent from "@/components/common/MonthPickerComponent.vue";
import { useValidators } from "@/composables/useValidators";
import CnmcFieldErrors from "./CnmcFieldErrors.vue";
import type { CnmcDevice, CnmcProductionInput } from "./cnmcTypes";

const props = defineProps<{ devices: CnmcDevice[]; busy: boolean }>();
const row = defineModel<CnmcProductionInput>({ required: true });
const emit = defineEmits<{ remove: [] }>();
const { t } = useI18n();
const validators = useValidators(t);
const v$ = useVuelidate(
  {
    registry_device_id: {
      required: helpers.withMessage(
        () => t("validations.required"),
        (value: number) =>
          props.devices.some(
            (device) => device.id === value && !device.preparation_error
          )
      ),
    },
    month: {
      required: validators.required,
      format: helpers.withMessage(
        () => t("cnmc.validation.month"),
        helpers.regex(/^[0-9]{4}-(0[1-9]|1[0-2])-01$/)
      ),
    },
    value: {
      required: validators.required,
      min: validators.min(0),
      max: validators.max(9999999.999),
      format: helpers.withMessage(
        () => t("cnmc.validation.production_precision"),
        helpers.regex(/^-?(?:[0-9]+(?:\.[0-9]{0,3})?|\.[0-9]{1,3})$/)
      ),
    },
  },
  row,
  { $autoDirty: true }
);
const pickMonth = (value: string) => {
  row.value.month = value ? `${value}-01` : "";
  v$.value.month.$touch();
};
</script>

<template>
  <tr class="align-top">
    <td>
      <select
        v-model="row.registry_device_id"
        class="form-select form-select-sm"
        :class="{ 'is-invalid': v$.registry_device_id.$error }"
        :aria-invalid="v$.registry_device_id.$error"
        @blur="v$.registry_device_id.$touch()"
        :aria-label="$t('cnmc.device')"
        :disabled="Boolean(row.production_data_id) || busy"
      >
        <option
          v-for="device in devices"
          :key="device.id"
          :value="device.id"
          :disabled="Boolean(device.preparation_error)"
        >
          {{ device.name || device.cil || `#${device.id}` }}
          {{ device.preparation_error ? ` — ${device.preparation_error}` : "" }}
        </option>
      </select>
      <CnmcFieldErrors :validation="v$.registry_device_id" />
      <div class="small text-body-secondary text-break mt-1 ps-3">
        {{
          devices.find((device) => device.id === row.registry_device_id)?.cil
        }}
      </div>
    </td>
    <td>
      <MonthPickerComponent
        :modelValue="row.month.slice(0, 7)"
        :invalid="v$.month.$error"
        :ariaLabel="$t('cnmc.month')"
        :disabled="Boolean(row.production_data_id) || busy"
        @update:modelValue="pickMonth"
        @blur="v$.month.$touch()"
      />
      <CnmcFieldErrors :validation="v$.month" />
    </td>
    <td>
      <input
        :value="row.value"
        @input="row.value = ($event.target as HTMLInputElement).value"
        type="number"
        min="0"
        step="0.001"
        class="form-control form-control-sm"
        :class="{ 'is-invalid': v$.value.$error }"
        :aria-invalid="v$.value.$error"
        @blur="v$.value.$touch()"
        :aria-label="$t('cnmc.production_mwh')"
        :readonly="Boolean(row.production_data_id)"
        :disabled="busy"
      />
      <CnmcFieldErrors :validation="v$.value" />
    </td>
    <td>
      <button
        type="button"
        class="btn btn-link text-danger p-1 mt-1"
        :aria-label="$t('common.remove')"
        :disabled="busy"
        @click="emit('remove')"
      >
        <FontAwesomeIcon icon="xmark" />
      </button>
    </td>
  </tr>
</template>
